import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  FileText, 
  Shield, 
  CreditCard,
  ChevronRight,
  ArrowLeft
} from "lucide-react";
import Navbar from "../components/Navbar";

const Terms = () => {
  const [activeSection, setActiveSection] = useState("terms");

  const sections = [
    { id: "terms", label: "Terms of Service", icon: FileText },
    { id: "privacy", label: "Privacy Policy", icon: Shield },
    { id: "refund", label: "Refund Policy", icon: CreditCard },
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="min-h-screen bg-[#020408]">
      <Navbar />

      <main className="pt-24 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Back Button */}
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-primary-main transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          {/* Header */}
          <div className="mb-12">
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Terms & <span className="text-primary-main">Conditions</span>
            </h1>
            <p className="text-gray-400 text-lg">
              Last updated: January 2025
            </p>
            <p className="text-gray-500 text-sm mt-2">
              VendorCompare is a product of <strong className="text-gray-400">Cubein Ltd</strong>
            </p>
          </div>

          {/* Navigation Tabs */}
          <div className="glass rounded-xl p-2 mb-12 flex flex-wrap gap-2">
            {sections.map((section) => (
              <Button
                key={section.id}
                variant={activeSection === section.id ? "default" : "ghost"}
                onClick={() => scrollToSection(section.id)}
                className={`flex-1 min-w-[150px] ${
                  activeSection === section.id
                    ? "bg-primary-main text-black"
                    : "text-gray-400 hover:text-white hover:bg-background-subtle"
                }`}
                data-testid={`nav-${section.id}`}
              >
                <section.icon className="w-4 h-4 mr-2" />
                {section.label}
              </Button>
            ))}
          </div>

          {/* Content */}
          <div className="space-y-16">
            {/* Terms of Service */}
            <section id="terms" className="scroll-mt-32">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-primary-main/20 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-primary-main" />
                </div>
                <h2 className="font-display text-3xl font-bold">Terms of Service</h2>
              </div>

              <div className="glass rounded-xl p-8 space-y-6">
                <div className="p-4 bg-primary-main/10 border border-primary-main/30 rounded-lg">
                  <p className="text-gray-300 leading-relaxed">
                    These Terms of Service ("Terms") constitute a legally binding agreement between you and 
                    <strong className="text-white"> Cubein Ltd</strong> ("Company", "we", "us", or "our"), 
                    governing your access to and use of the VendorCompare platform and services.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-primary-main mb-3">1. Acceptance of Terms</h3>
                  <p className="text-gray-300 leading-relaxed">
                    By accessing or using VendorCompare ("Service"), operated by Cubein Ltd, you agree to be bound 
                    by these Terms of Service. If you do not agree to these terms, please do not use our Service. 
                    We reserve the right to modify these terms at any time, and your continued use of the Service 
                    constitutes acceptance of any changes.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-primary-main mb-3">2. Description of Service</h3>
                  <p className="text-gray-300 leading-relaxed">
                    VendorCompare is a SaaS platform operated by Cubein Ltd that enables users to upload, analyze, 
                    and compare vendor quotes using AI-powered analysis. The Service includes document parsing, 
                    price comparison, vendor analysis, and export functionality. Features may vary based on your 
                    subscription tier (Free, Standard, or Pro).
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-primary-main mb-3">3. User Accounts</h3>
                  <p className="text-gray-300 leading-relaxed mb-3">
                    To access certain features, you must create an account. You agree to:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li>Provide accurate and complete registration information</li>
                    <li>Maintain the security of your account credentials</li>
                    <li>Accept responsibility for all activities under your account</li>
                    <li>Notify us immediately of any unauthorized access</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-primary-main mb-3">4. Acceptable Use</h3>
                  <p className="text-gray-300 leading-relaxed mb-3">
                    You agree not to:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li>Upload malicious files or content that could harm our systems</li>
                    <li>Attempt to gain unauthorized access to any part of the Service</li>
                    <li>Use the Service for any illegal or unauthorized purpose</li>
                    <li>Resell or redistribute the Service without authorization</li>
                    <li>Interfere with or disrupt the Service or servers</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-primary-main mb-3">5. Intellectual Property</h3>
                  <p className="text-gray-300 leading-relaxed">
                    The Service, including its original content, features, and functionality, is owned by 
                    Cubein Ltd and is protected by international copyright, trademark, and other intellectual 
                    property laws. "VendorCompare" is a trademark of Cubein Ltd. Your uploaded documents remain 
                    your property; however, you grant us a limited license to process them for providing the Service.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-primary-main mb-3">6. Subscription and Billing</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Paid subscriptions are billed in advance on a monthly basis through our payment processor, 
                    Paddle.com, who acts as the Merchant of Record. You authorize the applicable charges for 
                    subscription fees. Subscriptions automatically renew unless cancelled before the renewal date. 
                    Pricing is subject to change with 30 days notice.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-primary-main mb-3">7. Limitation of Liability</h3>
                  <p className="text-gray-300 leading-relaxed">
                    To the maximum extent permitted by law, Cubein Ltd and its directors, employees, and agents 
                    shall not be liable for any indirect, incidental, special, consequential, or punitive damages 
                    resulting from your use of the Service. Our total liability shall not exceed the amount paid 
                    by you for the Service in the twelve months preceding the claim.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-primary-main mb-3">8. Termination</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Cubein Ltd may terminate or suspend your account at any time for violations of these terms. 
                    You may cancel your account at any time through your account settings. Upon termination, 
                    your right to use the Service will cease immediately.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-primary-main mb-3">9. Governing Law</h3>
                  <p className="text-gray-300 leading-relaxed">
                    These Terms shall be governed by and construed in accordance with the laws of the jurisdiction 
                    in which Cubein Ltd is registered, without regard to its conflict of law provisions. Any disputes 
                    arising from these Terms or your use of the Service shall be subject to the exclusive jurisdiction 
                    of the courts in that jurisdiction.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-primary-main mb-3">10. Contact Information</h3>
                  <p className="text-gray-300 leading-relaxed">
                    For any questions regarding these Terms of Service, please contact us at:
                  </p>
                  <div className="mt-3 p-4 bg-background-subtle rounded-lg">
                    <p className="text-white font-semibold">Cubein Ltd</p>
                    <p className="text-gray-400">Email: legal@vendorcompare.com</p>
                    <p className="text-gray-400">Support: support@vendorcompare.com</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Privacy Policy */}
            <section id="privacy" className="scroll-mt-32">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-secondary-main/20 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-secondary-main" />
                </div>
                <h2 className="font-display text-3xl font-bold">Privacy Policy</h2>
              </div>

              <div className="glass rounded-xl p-8 space-y-6">
                <div className="p-4 bg-secondary-main/10 border border-secondary-main/30 rounded-lg">
                  <p className="text-gray-300 leading-relaxed">
                    This Privacy Policy explains how <strong className="text-white">Cubein Ltd</strong> ("Company", 
                    "we", "us", or "our") collects, uses, and protects your personal information when you use 
                    VendorCompare ("Service").
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-secondary-main mb-3">1. Information We Collect</h3>
                  <p className="text-gray-300 leading-relaxed mb-3">
                    We collect information you provide directly:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li><strong>Account Information:</strong> Name, email address, profile picture (via Google OAuth)</li>
                    <li><strong>Uploaded Documents:</strong> Vendor quotes and files you upload for comparison</li>
                    <li><strong>Usage Data:</strong> How you interact with our Service, features used, comparisons created</li>
                    <li><strong>Payment Information:</strong> Billing details processed securely through Paddle (our payment provider)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-secondary-main mb-3">2. How We Use Your Information</h3>
                  <p className="text-gray-300 leading-relaxed mb-3">
                    We use collected information to:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li>Provide, maintain, and improve our Service</li>
                    <li>Process your documents using AI for vendor analysis</li>
                    <li>Send you technical notices and support messages</li>
                    <li>Respond to your comments and questions</li>
                    <li>Monitor and analyze usage trends</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-secondary-main mb-3">3. Data Storage and Security</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Your data is stored on secure servers with encryption at rest and in transit. We implement 
                    industry-standard security measures including SSL/TLS encryption, secure authentication, 
                    and regular security audits. Your uploaded documents are processed for analysis and stored 
                    only as long as necessary to provide the Service.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-secondary-main mb-3">4. Third-Party Services</h3>
                  <p className="text-gray-300 leading-relaxed mb-3">
                    We use trusted third-party services:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li><strong>Google OAuth:</strong> For secure authentication</li>
                    <li><strong>AI Processing:</strong> Document analysis through secure API connections</li>
                    <li><strong>Payment Processing:</strong> Secure payment handling (we never store full card details)</li>
                    <li><strong>Cloud Hosting:</strong> Secure infrastructure for data storage</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-secondary-main mb-3">5. Data Sharing</h3>
                  <p className="text-gray-300 leading-relaxed">
                    We do not sell your personal information. We may share data only: (a) with your consent, 
                    (b) to comply with legal obligations, (c) to protect our rights and safety, or (d) with 
                    service providers who assist in operating our Service under strict confidentiality agreements.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-secondary-main mb-3">6. Your Rights</h3>
                  <p className="text-gray-300 leading-relaxed mb-3">
                    You have the right to:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li>Access and receive a copy of your personal data</li>
                    <li>Correct inaccurate personal information</li>
                    <li>Request deletion of your account and data</li>
                    <li>Object to or restrict certain processing</li>
                    <li>Data portability where technically feasible</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-secondary-main mb-3">7. Cookies</h3>
                  <p className="text-gray-300 leading-relaxed">
                    We use essential cookies for authentication and session management. These are necessary 
                    for the Service to function. We do not use advertising or tracking cookies. You can 
                    manage cookie preferences through your browser settings.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-secondary-main mb-3">8. Data Controller</h3>
                  <p className="text-gray-300 leading-relaxed">
                    The data controller responsible for your personal information is:
                  </p>
                  <div className="mt-3 p-4 bg-background-subtle rounded-lg">
                    <p className="text-white font-semibold">Cubein Ltd</p>
                    <p className="text-gray-400">Email: privacy@vendorcompare.com</p>
                  </div>
                  <p className="text-gray-300 leading-relaxed mt-3">
                    For privacy-related inquiries or to exercise your rights, please contact us at the email above. 
                    We will respond to your request within 30 days.
                  </p>
                </div>
              </div>
            </section>

            {/* Refund Policy */}
            <section id="refund" className="scroll-mt-32">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-accent-success/20 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-accent-success" />
                </div>
                <h2 className="font-display text-3xl font-bold">Refund Policy</h2>
              </div>

              <div className="glass rounded-xl p-8 space-y-6">
                <div className="p-4 bg-accent-success/10 border border-accent-success/30 rounded-lg">
                  <p className="text-gray-300 leading-relaxed">
                    <strong className="text-white">Payment Processing:</strong> All payments for VendorCompare are 
                    processed by <strong className="text-accent-success">Paddle.com</strong>, our Merchant of Record. 
                    Paddle handles all billing, payments, tax compliance, and refund processing on our behalf.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-accent-success mb-3">1. Free Tier</h3>
                  <p className="text-gray-300 leading-relaxed">
                    VendorCompare offers a free tier that allows you to explore the Service before committing 
                    to a paid subscription. We encourage all users to fully utilize the free tier to ensure 
                    the Service meets their needs before upgrading.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-accent-success mb-3">2. EU Consumer Rights</h3>
                  <p className="text-gray-300 leading-relaxed">
                    If you are a consumer in the European Union, you have the right to cancel your subscription 
                    within <strong className="text-white">14 days</strong> of purchase without giving any reason, 
                    in accordance with EU consumer protection laws. This right applies to new subscriptions only. 
                    Refunds will be processed to your original payment method.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-accent-success mb-3">3. Refund Eligibility</h3>
                  <p className="text-gray-300 leading-relaxed mb-3">
                    You may be eligible for a refund if:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li>You request within <strong className="text-white">14 days</strong> of your initial purchase (EU cancellation right)</li>
                    <li>For card payments: refund requests must be made within <strong className="text-white">120 days</strong> of the transaction</li>
                    <li>For PayPal payments: refund requests must be made within <strong className="text-white">179 days</strong> of the transaction</li>
                    <li>Your account has not been terminated for Terms of Service violations</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-accent-success mb-3">4. How to Request a Refund</h3>
                  <p className="text-gray-300 leading-relaxed mb-3">
                    To request a refund:
                  </p>
                  <ol className="list-decimal list-inside text-gray-300 space-y-2 ml-4">
                    <li>Email us at support@vendorcompare.com with your account email and transaction ID</li>
                    <li>Include "Refund Request" in the subject line</li>
                    <li>Provide the reason for your refund request</li>
                    <li>We will review and process eligible requests through Paddle</li>
                  </ol>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-accent-success mb-3">5. Refund Processing</h3>
                  <p className="text-gray-300 leading-relaxed mb-3">
                    Once approved, refunds are processed by Paddle:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li><strong className="text-white">Card payments:</strong> 3-5 business days</li>
                    <li><strong className="text-white">PayPal:</strong> Within 48 hours</li>
                    <li>Refunds are returned to the original payment method used for the purchase</li>
                    <li>Upon refund, your account will be downgraded to the Free tier</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-accent-success mb-3">6. Subscription Cancellation</h3>
                  <p className="text-gray-300 leading-relaxed">
                    You can cancel your subscription at any time from your account settings or through Paddle's 
                    subscription management. Upon cancellation, you'll continue to have access to paid features 
                    until the end of your current billing period. Subscriptions can be easily cancelled without 
                    contacting support.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-accent-success mb-3">7. Exceptions & Limitations</h3>
                  <p className="text-gray-300 leading-relaxed mb-3">
                    Refunds may not be granted in the following cases:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li>Account termination due to Terms of Service violations</li>
                    <li>Fraudulent activity, abuse, or manipulation of the refund policy</li>
                    <li>Requests made after the applicable refund window (120 days for cards, 179 days for PayPal)</li>
                    <li>Digital content that has already been accessed or downloaded</li>
                    <li>Chargebacks filed without first contacting our support team</li>
                  </ul>
                  <p className="text-gray-400 text-sm mt-3">
                    Note: Refund decisions are made at the discretion of VendorCompare and Paddle on a case-by-case basis.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-accent-success mb-3">8. Sales Tax Refunds</h3>
                  <p className="text-gray-300 leading-relaxed">
                    If you believe you were incorrectly charged sales tax or VAT, please contact us within 
                    <strong className="text-white"> 60 days</strong> of the transaction with a valid tax exemption 
                    certificate or proof of tax-exempt status. Paddle, as the Merchant of Record, handles all 
                    tax compliance and will process eligible tax refunds.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-accent-success mb-3">9. Contact Support</h3>
                  <p className="text-gray-300 leading-relaxed">
                    If you have any questions about our refund policy or need assistance, please contact 
                    our support team at support@vendorcompare.com. For payment-specific inquiries, you may 
                    also contact Paddle directly through the receipt email you received upon purchase.
                  </p>
                </div>

                <div className="p-4 bg-background-subtle border border-border rounded-lg">
                  <p className="text-gray-400 text-sm">
                    <strong className="text-gray-300">Paddle Terms:</strong> All purchases are subject to 
                    Paddle's Checkout Buyer Terms available at{" "}
                    <a 
                      href="https://www.paddle.com/legal/checkout-buyer-terms" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-main hover:underline"
                    >
                      paddle.com/legal/checkout-buyer-terms
                    </a>
                  </p>
                </div>
              </div>
            </section>
          </div>

          {/* Footer CTA */}
          <div className="mt-16 glass rounded-xl p-8 text-center">
            <h3 className="font-display text-2xl font-semibold mb-3">Have Questions?</h3>
            <p className="text-gray-400 mb-6">
              Our support team is ready to help with any concerns about our terms and policies.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                variant="outline" 
                className="border-primary-main text-primary-main hover:bg-primary-main/10"
                onClick={() => window.location.href = 'mailto:support@vendorcompare.com'}
              >
                Contact Support
              </Button>
              <Link to="/signup">
                <Button className="bg-primary-main text-black hover:bg-primary-light">
                  Get Started Free
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Terms;
